import { useState } from 'react';
import { Clock } from '../components/Clock';
import { ComicBoard, comicSrc } from '../components/ComicBoard';
import { JokerBanner } from '../components/JokerBanner';
import { WritingSurface } from '../components/WritingSurface';
import { beamFont } from '../../shared/fonts';
import { COMIC_PANELS } from '../../shared/types';
import { remainingNow, useSession, useTicker } from '../lib/socket';
import { useGameSounds } from '../lib/useGameSounds';
import { storeAuthorToken, storedAuthorToken } from '../lib/router';

/**
 * Author device (R-1): own clock, opponent clock, the writing surface, the
 * active joker, and the joint-completion confirmation (D3).
 *
 * The device follows the moderation's live typography (2026-09-08) so the
 * authors read their text in the same face and size the audience sees, and
 * it shows the same task slide as the beamer before the clocks start.
 */
export function AuthorView({ pin }: { pin: string }) {
  const [token, setToken] = useState<string | null>(() => storedAuthorToken(pin));
  const [name, setName] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  const session = useSession(pin, 'author', {
    token,
    name,
    enabled: token !== null || name !== null,
    onToken: (t) => {
      storeAuthorToken(pin, t);
      setToken(t);
    },
  });
  const now = useTicker(200);
  const sound = useGameSounds(session.state, 'author');

  if (token === null && name === null) {
    return (
      <div className="join">
        <header className="masthead masthead--small">
          <h1>Schreibarena</h1>
        </header>
        <div className="card join__card">
          <span className="card__no">PIN {pin}</span>
          <h2>Wer schreibt hier?</h2>
          <form
            className="pin-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (nameDraft.trim()) setName(nameDraft.trim());
            }}
          >
            <input
              className="input"
              autoFocus
              placeholder="Dein Name"
              maxLength={40}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              aria-label="Autor:innen-Name"
              data-testid="author-name-input"
            />
            <button className="btn btn--primary" type="submit" disabled={!nameDraft.trim()} data-testid="author-join">
              Platz nehmen
            </button>
          </form>
          <p className="join__hint">Wer zuerst beitritt, ist Autor:in 1. Physische Tastatur nicht vergessen.</p>
        </div>
      </div>
    );
  }

  if (session.fatalError) {
    return <FullMessage title="Kein Zutritt" text={session.fatalError} />;
  }
  if (!session.state || session.authorIndex === null) {
    return <FullMessage title="Verbinden…" text={`Session ${pin}`} />;
  }

  const state = session.state;
  const round = state.round;
  const me = session.authorIndex;
  const opponent = me === 0 ? 1 : 0;
  const myTurn = round.phase === 'running' && round.activeAuthor === me;
  const confirmed = round.confirmedDone[me] === true;
  const canConfirm = round.phase === 'running' || round.phase === 'paused';
  const comic = round.comicId === null ? undefined : state.comics.find((c) => c.id === round.comicId);
  const roundLabel = round.kind === 'comic' ? `Bildrunde ${round.number}` : `Runde ${round.number}`;
  const panel = Math.min(round.panelStarts.length, COMIC_PANELS);
  // Live typography from the moderation reaches the author device too.
  const typography = {
    '--story-font': beamFont(state.display.fontId).stack,
    '--story-scale': state.display.fontScale,
  } as React.CSSProperties;

  return (
    <div className="author" style={typography}>
      <header className="author__head">
        <div className="author__brand">
          Schreibarena
          <span className="author__round">{roundLabel}</span>
          {round.kind === 'comic' && (round.phase === 'running' || round.phase === 'paused') && (
            <span className="author__round" data-testid="panel-counter">
              Panel {panel} / {COMIC_PANELS}
            </span>
          )}
          {session.status !== 'ready' && <span className="badge badge--warn">Verbindung…</span>}
        </div>
        <div className="author__clocks">
          <Clock
            seat={state.authors[opponent]}
            seatIndex={opponent}
            round={round}
            remainingMs={remainingNow(state, session.stateReceivedAt, now, opponent)}
            size="S"
          />
          <Clock
            seat={state.authors[me]}
            seatIndex={me}
            round={round}
            remainingMs={remainingNow(state, session.stateReceivedAt, now, me)}
            size="M"
          />
        </div>
      </header>

      <div className={`turnbar ${myTurn ? 'turnbar--live' : ''}`} data-testid="turn-banner">
        {round.phase === 'idle' && 'Warten auf den Start…'}
        {round.phase === 'intro' &&
          (round.activeAuthor === me
            ? 'GLEICH GEHT ES LOS — du beginnst. Die Moderation startet die Uhren.'
            : `Gleich geht es los — ${state.authors[round.activeAuthor]?.name ?? '…'} beginnt.`)}
        {round.phase === 'paused' && 'PAUSE — die Moderation hat das Spiel angehalten.'}
        {round.phase === 'ended' && `ENDE — ${endLabel(round.endReason, round.kind)}`}
        {round.phase === 'running' &&
          (myTurn
            ? round.kind === 'comic'
              ? `DU BIST DRAN — Panel ${panel} beschriften, Shift+Enter gibt ab.`
              : 'DU BIST DRAN — Shift+Enter gibt ab.'
            : `${state.authors[round.activeAuthor]?.name ?? '…'} schreibt.`)}
      </div>

      <JokerBanner
        round={round}
        authors={state.authors}
        now={now}
        clockOffset={session.clockOffset}
      />

      {round.prompt && <div className="prompt">Impuls: {round.prompt}</div>}

      {round.phase === 'intro' ? (
        <div className="author__task" data-testid="author-task">
          {round.kind === 'comic' && comic ? (
            <ComicBoard
              comic={comic}
              src={comicSrc(pin, comic.id)}
              round={round}
              authors={state.authors}
              captions={false}
              className="comicboard--intro"
            />
          ) : (
            <div className="author__task-text">{round.prompt || roundLabel}</div>
          )}
        </div>
      ) : (
        <WritingSurface
          round={round}
          authors={state.authors}
          authorIndex={me}
          send={session.send}
          comic={comic ? { def: comic, src: comicSrc(pin, comic.id) } : null}
        />
      )}

      <footer className="author__foot">
        <div className="author__hints">
          <span>
            <kbd>Shift</kbd>+<kbd>Enter</kbd>{' '}
            {round.kind === 'comic' ? 'Panel fertig, abgeben' : 'Zug abgeben'}
          </span>
          <button
            className="btn btn--ghost btn--small"
            onClick={() => sound.setEnabled(!sound.enabled)}
            data-testid="sound-toggle"
          >
            {sound.enabled ? '🔊 Ton an' : '🔇 Ton aus'}
          </button>
        </div>
        {canConfirm && (
          <button
            className={`btn ${confirmed ? 'btn--confirmed' : ''}`}
            onClick={() => session.send({ type: 'confirm_done', value: !confirmed })}
            data-testid="confirm-done"
          >
            {confirmed ? 'Fertig ✓ (zurücknehmen)' : 'Geschichte für fertig erklären'}
          </button>
        )}
      </footer>

      {session.denied && (
        <div className="toast" role="status" data-testid="denied-toast">
          {session.denied}
        </div>
      )}
    </div>
  );
}

function endLabel(reason: string | null, kind: 'story' | 'comic'): string {
  switch (reason) {
    case 'time':
      return 'beide Uhren auf 0:00.';
    case 'completed':
      return kind === 'comic' ? 'alle sechs Panels beschriftet.' : 'gemeinsam für fertig erklärt.';
    case 'forced':
      return 'von der Moderation beendet.';
    default:
      return '';
  }
}

function FullMessage({ title, text }: { title: string; text: string }) {
  return (
    <div className="fullmsg">
      <h1>{title}</h1>
      <p>{text}</p>
      <a className="btn btn--ghost" href="#/">
        Zur Startseite
      </a>
    </div>
  );
}
